import { useEffect, useState } from "react";
import { Box, Grid, Card, CardContent, Typography, Divider, CircularProgress, Alert, Chip, Stack, TextField, Button, Paper, CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DirectionsCar, PersonOff, AttachMoney, TrendingUp, Today, DateRange, EventAvailable, MoneyOff, Refresh, Margin } from "@mui/icons-material";
import style from "./TaxiDashboard.module.scss";
import { useNavigate } from "react-router-dom";

import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const theme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: '"Cairo", "Tajawal", Arial, sans-serif',
  },
  palette: {
    background: { default: "#fbfbfb" },
  },
});

const API_BASE = "https://dash.tatabu.io/back-end/public/api/taxi-orders";

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: 3,
      border: "1px solid",
      borderColor: "grey.200",
      borderRight: `4px solid ${color}`,
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
    }}
  >
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={800} color={color} sx={{ fontSize: { xs: "1.6rem", sm: "2rem" } }}>
            {value ?? <CircularProgress size={28} sx={{ color }} />}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}18`,
            borderRadius: 2,
            p: { xs: 0.8, sm: 1.2 },
            display: "flex",
            flexShrink: 0,
            ml: 1,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default function TaxiDashboard() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [analytics, setAnalytics] = useState(null);
  const [general, setGeneral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchAnalytics = async () => {
    const res = await fetch(`${API_BASE}/analytics`);
    const json = await res.json();
    if (json.success) setAnalytics(json.data);
  };

  const fetchGeneral = async (from = dateFrom, to = dateTo) => {
    const params = new URLSearchParams();
    if (from) params.append("date_from", from);
    if (to) params.append("date_to", to);
    const res = await fetch(`${API_BASE}/general-analytics?${params}`);
    const json = await res.json();
    if (json.success) setGeneral(json.data);
  };

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchAnalytics(), fetchGeneral("", "")]);
    } catch {
      setError("حدث خطأ أثناء تحميل البيانات");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleFilter = async () => {
    setError(null);
    try {
      await fetchGeneral();
    } catch {
      setError("فشل في تطبيق الفلتر");
    }
  };

  const handleReset = () => {
    setDateFrom("");
    setDateTo("");
    fetchAll();
  };

  const formatRiyal = (val) => (val != null ? `${Number(val).toLocaleString("en-SA")} ﷼` : null);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <Box dir="rtl" sx={{ bgcolor: "background.default", minHeight: "100vh", p: { xs: 1, sm: 3, md: 4 } }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Header */}
          <Stack direction={{ xs: "row", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: { xs: 2, sm: 4 } }} gap={2}>
            <Button
              dir="ltr"
              startIcon={<Refresh />}
              variant="outlined"
              onClick={fetchAll}
              disabled={loading}
              size="small"
              sx={{ whiteSpace: "nowrap", alignSelf: { xs: "flex-end", sm: "auto" } }}
            >
              {loading ? "جاري التحميل..." : "تحديث"}
            </Button>
            <Box dir="ltr">
              <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}>
                Dashboard
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* ── General Analytics ─────────────────── */}
          <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 4, borderRadius: 3 }}>
            {/* Section header + date filter */}
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} mb={3} gap={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  الإحصائيات العامة
                </Typography>
                {general?.date_range && general.date_range !== "all time" ? (
                  <Chip
                    dir="ltr"
                    size="small"
                    icon={<DateRange fontSize="small" />}
                    label={`${general.date_range.to} ← ${general.date_range.from}`}
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                ) : (
                  <Chip size="small" label="جميع الأوقات" variant="outlined" sx={{ mt: 0.5 }} />
                )}
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems="stretch" sx={{ width: { xs: "100%", md: "auto" } }}>
                <TextField size="small" type="date" label="من" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ flex: 1 }} />
                <TextField size="small" type="date" label="إلى" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ flex: 1 }} />
                <Stack direction="row" gap={1}>
                  <Button variant="contained" size="small" onClick={handleFilter} disabled={!dateFrom || !dateTo} sx={{ flex: 1, whiteSpace: "nowrap" }}>
                    تطبيق
                  </Button>
                  <Button variant="outlined" size="small" onClick={handleReset} sx={{ flex: 1, whiteSpace: "nowrap" }}>
                    إعادة
                  </Button>
                </Stack>
              </Stack>
            </Stack>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="إجمالي الطلبات"
                  icon={<DirectionsCar sx={{ color: "#1976d2", fontSize: { xs: 20, sm: 24 } }} />}
                  value={general?.total_orders}
                  color="#1976d2"
                  subtitle="عدد جميع الطلبات"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="إجمالي الإيرادات"
                  icon={<AttachMoney sx={{ color: "#2e7d32", fontSize: { xs: 20, sm: 24 } }} />}
                  value={general ? formatRiyal(general.total_price) : null}
                  color="#2e7d32"
                  subtitle="مجموع أسعار الطلبات التي تم تنفيذها"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="مدفوعات السائقين"
                  icon={<MoneyOff sx={{ color: "#ed6c02", fontSize: { xs: 20, sm: 24 } }} />}
                  value={general ? formatRiyal(general.total_driver_amount) : null}
                  color="#ed6c02"
                  subtitle="مجموع مبالغ السائقين في الطلبات التي تم تنفيذها"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="صافي الربح"
                  icon={<TrendingUp sx={{ color: "#9c27b0", fontSize: { xs: 20, sm: 24 } }} />}
                  value={general ? formatRiyal(general.net_profit) : null}
                  color="#9c27b0"
                  subtitle="الإيرادات − مدفوعات السائقين (في الطلبات التي تم تنفيذها)"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Operations Analytics ──────────────── */}
          <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={3}>
              إحصائيات التشغيل
            </Typography>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box onClick={() => navigate("/dashboard/taxi/operation?tab=today")} sx={{ cursor: "pointer", height: "100%" }}>
                  <StatCard
                    title="طلبات اليوم"
                    icon={<Today sx={{ color: "#0288d1", fontSize: { xs: 20, sm: 24 } }} />}
                    value={analytics?.orders_pickup_today}
                    color="#0288d1"
                    subtitle="طلبات مجدولة اليوم"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box onClick={() => navigate("/dashboard/taxi/operation?tab=tomorrow")} sx={{ cursor: "pointer", height: "100%" }}>
                  <StatCard
                    title="طلبات الغد"
                    icon={<EventAvailable sx={{ color: "#00897b", fontSize: { xs: 20, sm: 24 } }} />}
                    value={analytics?.orders_pickup_tomorrow}
                    color="#00897b"
                    subtitle="طلبات مجدولة الغد"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box onClick={() => navigate("/dashboard/taxi/orders-without-drivers")} sx={{ cursor: "pointer", height: "100%" }}>
                  <StatCard
                    title="طلبات بدون سائق"
                    icon={<PersonOff sx={{ color: "#d32f2f", fontSize: { xs: 20, sm: 24 } }} />}
                    value={analytics?.orders_without_driver_info}
                    color="#d32f2f"
                    subtitle="طلبات بدون بيانات السائق (الاسم، رقم جوال، أو مبلغ السائق)"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box onClick={() => navigate("/dashboard/taxi/unscheduled-return-orders")} sx={{ cursor: "pointer", height: "100%" }}>
                  <StatCard
                    title="طلبات عودة غير مجدولة"
                    icon={<StarHalfIcon sx={{ color: "#9c27b0", fontSize: { xs: 20, sm: 24 } }} />}
                    value={analytics?.orders_needing_return_trip}
                    color="#9c27b0"
                    subtitle="طلبات ذهاب وعودة لم يتم جدولة رحلات العودة الخاصة بها بعد"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box onClick={() => navigate("/dashboard/taxi/expired-orders")} sx={{ cursor: "pointer", height: "100%" }}>
                  <StatCard
                    title="طلبات منتهية غير مغلقة"
                    icon={<WarningAmberRoundedIcon sx={{ color: "#f57c00", fontSize: { xs: 20, sm: 24 } }} />}
                    value={analytics?.orders_expired_not_resolved}
                    color="#f57c00"
                    subtitle="طلبات منتهية لكن لم يتم اغلاقها بعد"
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
